import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Calendar, LogOut, ArrowRight, Clock, XCircle, Home, Bell, RefreshCw, ChevronLeft, ChevronRight, Check, X as XIcon, Globe, Package, ExternalLink, ChevronDown, ChevronUp, Link2, FileText, Video } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RescheduleNotice } from "@/components/RescheduleNotice";
import { getTimezoneAbbr, formatTime12WithTz } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const tzAbbr = getTimezoneAbbr();

const formatTime12 = (t) => formatTime12WithTz(t, tzAbbr);

const VVLogo = () => (
  <img src="/vv-logo-mark.png" alt="VV" width={28} height={28} className="rounded-full" />
);

const TYPE_ICON = { link: "🔗", video: "🎬", pdf: "📄" };

const ResourceLink = ({ r }) => (
  <a
    key={r.id}
    href={r.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2.5 p-2.5 bg-[#D6E6FA]/40 rounded-lg border border-[#3C8DE4]/20 hover:bg-[#D6E6FA] hover:border-[#3C8DE4]/50 transition-all"
    data-testid={`student-resource-${r.id}`}
  >
    <span className="text-base leading-none">{TYPE_ICON[r.type] || "🔗"}</span>
    <span className="text-sm font-medium flex-1 text-[#0A0A0A]">{r.title}</span>
    <ExternalLink className="w-3.5 h-3.5 text-[#3C8DE4] flex-shrink-0" />
  </a>
);

const WeekGroup = ({ label, resources, lockBadge }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-[#0A0A0A]/10 rounded-xl overflow-hidden" data-testid={`week-group-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F0EFEB] hover:bg-[#E8E7E3] transition-colors text-left"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm">{label}</span>
          {lockBadge && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#D4EDDA] text-[#155724] font-bold">{lockBadge}</span>
          )}
          <span className="text-xs text-[#4A4A4A]">({resources.length} item{resources.length !== 1 ? "s" : ""})</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#4A4A4A] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#4A4A4A] flex-shrink-0" />}
      </button>
      {open && (
        <div className="p-3 space-y-1.5 bg-white">
          {resources.map(r => <ResourceLink key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
};

const ResourcePanel = ({ enrollmentId }) => {
  const [data, setData] = useState({ session_resources: [], student_resources: [], course_resources: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    axios.get(`${API}/student/resources/${enrollmentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  if (loading) return <p className="text-sm text-[#4A4A4A] py-2">Loading resources…</p>;

  const totalCount =
    data.session_resources.reduce((sum, s) => sum + s.resources.length, 0) +
    data.student_resources.length +
    data.course_resources.length;

  if (totalCount === 0) {
    return (
      <p className="text-sm text-[#4A4A4A] py-2 italic">
        No resources have been shared yet. Check back after your first attended class.
      </p>
    );
  }

  // Group course_resources by week_number for accordion display
  const courseGroups = [];
  const weekMap = {};
  data.course_resources.forEach(r => {
    const key = r.week_number == null ? "general" : `week-${r.week_number}`;
    if (!weekMap[key]) {
      weekMap[key] = {
        key,
        label: r.week_number == null ? "Course Materials" : `Week ${r.week_number}`,
        weekNum: r.week_number == null ? -1 : r.week_number,
        resources: []
      };
      courseGroups.push(weekMap[key]);
    }
    weekMap[key].resources.push(r);
  });
  courseGroups.sort((a, b) => a.weekNum === -1 ? -1 : b.weekNum === -1 ? 1 : a.weekNum - b.weekNum);

  return (
    <div className="space-y-5">
      {/* Course materials grouped by week (accordion) */}
      {courseGroups.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Course Materials</span>
            <span className="px-1.5 py-0.5 text-xs bg-[#D4EDDA] text-[#155724] rounded-full font-bold">For All Students</span>
          </div>
          <div className="space-y-2">
            {courseGroups.map(group => (
              <WeekGroup
                key={group.key}
                label={group.label}
                resources={group.resources}
                lockBadge={group.weekNum > 0 ? `Unlocked after Week ${group.weekNum}` : null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Student-specific materials (admin) */}
      {data.student_resources.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Your Materials</span>
            <span className="px-1.5 py-0.5 text-xs bg-[#FBE7C6] text-[#8B5E00] rounded-full font-bold">Personalized</span>
          </div>
          <div className="space-y-1.5">
            {data.student_resources.map(r => <ResourceLink key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {/* Session resources (per attended session — tutor/admin) */}
      {data.session_resources.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Session Resources</span>
            <span className="px-1.5 py-0.5 text-xs bg-[#D6E6FA] text-[#1A5DAD] rounded-full font-bold">After Class</span>
          </div>
          <div className="space-y-4">
            {data.session_resources.map(s => (
              <div key={s.session_id}>
                <div className="text-sm font-bold text-[#4A4A4A] mb-1">Week {s.week_number}</div>
                {s.feedback && (
                  <div className="text-xs text-[#4A4A4A] italic mb-2 pl-2 border-l-2 border-[#3C8DE4]">
                    "{s.feedback}"
                  </div>
                )}
                <div className="space-y-1.5">
                  {s.resources.map(r => <ResourceLink key={r.id} r={r} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDay, setRescheduleDay] = useState(0);
  const [rescheduleTime, setRescheduleTime] = useState("09:00");
  const [rescheduling, setRescheduling] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [showResources, setShowResources] = useState(null);

  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadEnrollments();
    loadSessions();
    checkNotifPermission();
  }, []);

  // Notification check every 60s
  useEffect(() => {
    if (!notifEnabled) return;
    const interval = setInterval(() => checkUpcomingNotifications(), 60000);
    checkUpcomingNotifications();
    return () => clearInterval(interval);
  }, [notifEnabled, sessions]);

  const checkNotifPermission = () => {
    if ("Notification" in window && Notification.permission === "granted") {
      setNotifEnabled(true);
    }
  };

  const enableNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error("Notifications not supported in this browser");
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      setNotifEnabled(true);
      toast.success("Notifications enabled! You'll be notified 1 hour before class.");
    } else {
      toast.error("Notification permission denied");
    }
  };

  const checkUpcomingNotifications = useCallback(() => {
    if (!notifEnabled || !sessions.length) return;
    const now = new Date();
    sessions.forEach((s) => {
      if (s.attended !== null) return;
      const [y, m, d] = s.scheduled_date.split("-").map(Number);
      const [h, min] = s.scheduled_time.split(":").map(Number);
      const classTime = new Date(y, m - 1, d, h, min);
      const diff = classTime - now;
      // Notify if class is 55-65 min away (check runs every 60s)
      if (diff > 0 && diff <= 65 * 60 * 1000 && diff > 55 * 60 * 1000) {
        new Notification("Virtually Verbal - Class in 1 hour!", {
          body: `${s.course_name} (Week ${s.week_number}) with ${s.tutor_name || "your tutor"} at ${formatTime12(s.scheduled_time)}`,
          icon: "/vv-logo.png",
        });
      }
    });
  }, [notifEnabled, sessions]);

  const loadEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/enrollments/my`, { headers });
      setEnrollments(response.data);
    } catch { toast.error("Failed to load enrollments"); }
    finally { setLoading(false); }
  };

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/class-sessions/my-upcoming`, { headers });
      setSessions(response.data);
    } catch { /* silent */ }
  };

  const cancelEnrollment = async () => {
    if (!cancelTarget) return;
    try {
      await axios.delete(`${API}/enrollments/${cancelTarget}`, { headers });
      toast.success("Lesson cancelled");
      setCancelTarget(null);
      loadEnrollments();
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget) return;
    setRescheduling(true);
    try {
      await axios.put(`${API}/enrollments/${rescheduleTarget}/reschedule`, {
        scheduled_day: rescheduleDay,
        scheduled_time: rescheduleTime,
      }, { headers });
      toast.success("Class rescheduled!");
      setRescheduleTarget(null);
      loadEnrollments();
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    } finally {
      setRescheduling(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  // Calendar helpers
  const getMonthDays = (d) => {
    const year = d.getFullYear(), month = d.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startPad = (first.getDay() + 6) % 7; // Mon=0
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let i = 1; i <= last.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const sessionsForDate = (d) => {
    if (!d) return [];
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return sessions.filter((s) => s.scheduled_date === iso);
  };

  const todayStr = new Date().toISOString().split("T")[0];
  const upcomingSessions = sessions.filter((s) => s.scheduled_date >= todayStr && s.attended === null).slice(0, 5);
  const calDays = getMonthDays(calMonth);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b-2 border-[#0A0A0A] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-home-link">
            <VVLogo />
            <span className="font-bold text-xl">Virtually Verbal</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#3C8DE4] hover:underline font-semibold hidden sm:flex items-center gap-1" data-testid="home-nav-link">
              <Home className="w-4 h-4" /> Home
            </Link>
            <span className="text-sm text-[#4A4A4A]">Hello, {user?.name?.split(" ")[0]}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <RescheduleNotice />
        {/* Welcome */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-1">Welcome back, {user?.name?.split(" ")[0]}!</h1>
            <p className="text-[#4A4A4A]">Continue your language learning journey</p>
          </div>
          {!notifEnabled && (
            <button onClick={enableNotifications} className="flex items-center gap-2 text-sm brutalist-btn-secondary" data-testid="enable-notif-btn">
              <Bell className="w-4 h-4" /> Enable Class Reminders
            </button>
          )}
          {notifEnabled && (
            <span className="flex items-center gap-1 text-xs text-green-600 font-semibold"><Bell className="w-3.5 h-3.5" /> Reminders on</span>
          )}
          <span className="flex items-center gap-1 text-xs text-[#3C8DE4] font-medium" data-testid="dashboard-timezone">
            <Globe className="w-3.5 h-3.5" /> {tzAbbr}
          </span>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link to="/courses" className="brutalist-card p-6 flex items-center gap-4 hover:bg-[#D6E6FA]" data-testid="browse-courses-link">
            <div className="w-12 h-12 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center">
              <BookOpen className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">Browse Courses</h3>
              <p className="text-sm text-[#4A4A4A]">Find new courses to enroll in</p>
            </div>
            <ArrowRight className="w-5 h-5" />
          </Link>
          <div className="brutalist-card p-6 flex items-center gap-4 bg-[#FBE7C6]">
            <div className="w-12 h-12 rounded-full bg-white border-2 border-[#0A0A0A] flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">8-Week Courses</h3>
              <p className="text-sm text-[#4A4A4A]">1 class/week, 1 hour each</p>
            </div>
          </div>
        </div>

        {/* Calendar + Upcoming */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Calendar */}
          <div className="lg:col-span-2 brutalist-card p-5" data-testid="class-calendar">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg flex items-center gap-2"><Calendar className="w-5 h-5" /> Class Calendar</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1))} className="p-1 hover:bg-[#D6E6FA] rounded" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
                <span className="text-sm font-semibold min-w-[120px] text-center">{calMonth.toLocaleString("default", { month: "long", year: "numeric" })}</span>
                <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1))} className="p-1 hover:bg-[#D6E6FA] rounded" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 text-center text-xs font-bold text-[#4A4A4A] mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calDays.map((d, i) => {
                if (!d) return <div key={i} />;
                const daySessions = sessionsForDate(d);
                const isToday = d.toISOString().split("T")[0] === todayStr;
                return (
                  <div key={i} className={`min-h-[48px] p-1 rounded-lg text-center text-xs ${isToday ? "bg-[#3C8DE4] text-white font-bold" : "hover:bg-[#D6E6FA]/50"}`} data-testid={`cal-day-${d.getDate()}`}>
                    <div>{d.getDate()}</div>
                    {daySessions.map((s, j) => (
                      <div key={j} className={`mt-0.5 text-[9px] leading-tight rounded px-0.5 truncate ${s.attended === true ? "bg-green-200 text-green-800" : s.attended === false ? "bg-red-200 text-red-800" : "bg-[#FBE7C6] text-[#8B5E00]"}`} title={`${s.course_name} W${s.week_number} ${formatTime12(s.scheduled_time)}`}>
                        {s.language_flag} W{s.week_number}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming Sessions */}
          <div className="brutalist-card p-5" data-testid="upcoming-sessions">
            <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> Upcoming Classes</h2>
            {upcomingSessions.length === 0 ? (
              <p className="text-sm text-[#4A4A4A]">No upcoming classes</p>
            ) : (
              <div className="space-y-3">
                {upcomingSessions.map((s) => {
                  const isCancelled = s.status === "cancelled";
                  const isReplacement = s.is_replacement || s.status === "replacement";
                  const sessionTypeLabel = { makeup: "Make-up", reschedule: "Reschedule", final_exam: "Final Exam" }[s.session_type];
                  return (
                    <div key={s.id} className={`p-3 rounded-lg border ${isCancelled ? "bg-[#FFAEBC]/20 border-[#FF6B6B]/30 opacity-70" : isReplacement ? "bg-[#FFF3CD] border-[#FFC107]/50" : "bg-[#D6E6FA]/30 border-[#0A0A0A]/10"}`} data-testid={`upcoming-${s.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{s.language_flag}</span>
                        <span className="text-sm font-bold">{s.course_name}</span>
                        {sessionTypeLabel && <span className="text-xs px-1.5 py-0.5 bg-[#FFF3CD] text-[#856404] rounded font-bold">{sessionTypeLabel}</span>}
                        {isCancelled && <span className="text-xs px-1.5 py-0.5 bg-[#FF6B6B] text-white rounded font-bold">CANCELLED</span>}
                        {isReplacement && !isCancelled && <span className="text-xs px-1.5 py-0.5 bg-[#FFC107] text-[#856404] rounded font-bold">REPLACEMENT</span>}
                      </div>
                      <div className={`text-xs text-[#4A4A4A] ${isCancelled ? "line-through" : ""}`}>
                        Week {s.week_number} &middot; {new Date(s.scheduled_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at {formatTime12(s.scheduled_time)}
                      </div>
                      {s.tutor_name && <div className="text-xs text-[#4A4A4A] mt-0.5">with {s.tutor_name}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* My Courses */}
        <div>
          <h2 className="text-2xl font-bold mb-4">My Courses</h2>
          
          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner"></div></div>
          ) : enrollments.length === 0 ? (
            <div className="brutalist-card p-12 text-center">
              <p className="text-[#4A4A4A] mb-4">You haven&apos;t enrolled in any courses yet</p>
              <Link to="/courses" className="brutalist-btn-primary inline-flex items-center gap-2">
                Browse Courses <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {enrollments.map((enrollment) => (
                <div key={enrollment.id} className="brutalist-card p-6" data-testid={`enrollment-${enrollment.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{enrollment.language_flag}</div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl">{enrollment.course_name}</h3>
                      <p className="text-sm text-[#4A4A4A]">
                        {enrollment.language_name} &middot; {enrollment.level}
                      </p>
                      {enrollment.tutor_name && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {enrollment.tutor_photo ? (
                            <img src={enrollment.tutor_photo} alt="" className="w-6 h-6 rounded-full border border-[#0A0A0A] object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-[#D6E6FA] border border-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                              {enrollment.tutor_name?.charAt(0)}
                            </div>
                          )}
                          <span className="text-sm text-[#4A4A4A]">{enrollment.tutor_name}</span>
                          {enrollment.scheduled_day !== null && enrollment.scheduled_time && (
                            <span className="text-sm text-[#4A4A4A]">
                              &middot; {DAY_NAMES[enrollment.scheduled_day]}s at {formatTime12(enrollment.scheduled_time)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-[#4A4A4A] mb-1">Progress</div>
                      <div className="font-bold text-lg">Week {enrollment.current_week} / {enrollment.duration_weeks}</div>
                    </div>
                  </div>
                  
                  <div className="mt-4 progress-bar h-3">
                    <div className="progress-bar-fill" style={{ width: `${(enrollment.current_week / enrollment.duration_weeks) * 100}%` }} />
                  </div>

                  {/* Holiday disclaimer */}
                  {enrollment.has_holiday_skips && (
                    <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FFF3E0] border border-[#FF9800] text-xs" data-testid={`holiday-note-${enrollment.id}`}>
                      <span className="text-[#E65100] shrink-0 mt-0.5">&#9888;</span>
                      <span className="text-[#E65100]">
                        <strong>Note:</strong> Some sessions have been moved due to tutor holidays
                        {enrollment.holiday_skip_reasons?.length > 0 && (
                          <> ({enrollment.holiday_skip_reasons.join(", ")})</>
                        )}. All {enrollment.duration_weeks} sessions are still included.
                      </span>
                    </div>
                  )}
                  
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link to={`/assignments/${enrollment.id}`} className="brutalist-btn-primary text-sm" data-testid={`view-assignments-${enrollment.id}`}>
                      View Assignments
                    </Link>
                    <Link to={`/courses/${enrollment.course_id}`} className="brutalist-btn-secondary text-sm">
                      Course Details
                    </Link>
                    <button
                      onClick={() => setShowResources(showResources === enrollment.id ? null : enrollment.id)}
                      className={`text-sm px-3 py-1 border-2 rounded-lg transition-colors flex items-center gap-1 ${
                        showResources === enrollment.id
                          ? 'border-[#3C8DE4] bg-[#D6E6FA] text-[#0A0A0A]'
                          : 'border-[#3C8DE4] text-[#3C8DE4] hover:bg-[#D6E6FA]'
                      }`}
                      data-testid={`extra-resources-${enrollment.id}`}
                    >
                      <Package className="w-4 h-4" />
                      Extra Resources
                      {showResources === enrollment.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => {
                        setRescheduleTarget(enrollment.id);
                        setRescheduleDay(enrollment.scheduled_day ?? 0);
                        setRescheduleTime(enrollment.scheduled_time ?? "09:00");
                      }}
                      className="text-sm px-3 py-1 border-2 border-[#3C8DE4] text-[#3C8DE4] rounded-lg hover:bg-[#D6E6FA] transition-colors flex items-center gap-1"
                      data-testid={`reschedule-${enrollment.id}`}
                    >
                      <RefreshCw className="w-4 h-4" /> Reschedule
                    </button>
                    <button
                      onClick={() => setCancelTarget(enrollment.id)}
                      className="text-sm px-3 py-1 border-2 border-[#FF6B6B] text-[#FF6B6B] rounded-lg hover:bg-[#FFAEBC] transition-colors flex items-center gap-1"
                      data-testid={`cancel-enrollment-${enrollment.id}`}
                    >
                      <XCircle className="w-4 h-4" /> Cancel
                    </button>
                  </div>

                  {showResources === enrollment.id && (
                    <div className="mt-4 pt-4 border-t-2 border-[#3C8DE4]/20" data-testid={`resources-panel-${enrollment.id}`}>
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2 text-[#3C8DE4]">
                        <Package className="w-4 h-4" /> Extra Resources
                      </h4>
                      <ResourcePanel enrollmentId={enrollment.id} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Cancel Dialog */}
          <ConfirmDialog
            open={!!cancelTarget}
            onOpenChange={(open) => !open && setCancelTarget(null)}
            title="Cancel This Lesson?"
            description="This will cancel your enrollment and remove all scheduled sessions. You can re-enroll later if slots are available."
            onConfirm={cancelEnrollment}
            destructive
          />

          {/* Reschedule Dialog */}
          {rescheduleTarget && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reschedule-dialog">
              <div className="brutalist-card p-6 max-w-md w-full bg-white">
                <h3 className="text-xl font-bold mb-4">Reschedule Class</h3>
                <p className="text-sm text-[#4A4A4A] mb-5">Choose a new day and time for your weekly class.</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wider">Day of Week</label>
                    <select value={rescheduleDay} onChange={(e) => setRescheduleDay(Number(e.target.value))} className="brutalist-input mt-1 w-full" data-testid="reschedule-day">
                      {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-bold uppercase tracking-wider">Time</label>
                    <input type="time" value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} className="brutalist-input mt-1 w-full" data-testid="reschedule-time" />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={handleReschedule} disabled={rescheduling} className="brutalist-btn-primary flex-1 flex items-center justify-center gap-2" data-testid="reschedule-confirm-btn">
                    {rescheduling ? "Saving..." : <><Check className="w-4 h-4" /> Confirm</>}
                  </button>
                  <button onClick={() => setRescheduleTarget(null)} className="brutalist-btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="reschedule-cancel-btn">
                    <XIcon className="w-4 h-4" /> Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
